import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { GAME_FONT, formatDerivedAccountLevel, type PhoneMetrics } from "../renderConstants";
import { resolveCharacterImagePath } from "../../economy/characters";

export interface PhoneFriendsDeps {
  drawPhoneFriendDetail(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, contentRight: number,
    metrics: PhoneMetrics, friend: RenderModel["friendsList"][number],
  ): void;
  drawPhoneAddFriendInline(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, contentRight: number,
    metrics: PhoneMetrics,
  ): void;
  drawPhoneReferFriend(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, contentRight: number,
    metrics: PhoneMetrics,
  ): void;
  drawNotificationPanel(
    rect: Rect,
    options: { fill: string; stroke?: string; accent?: string; radius?: number; lineWidth?: number },
  ): void;
  drawNotificationEmptyState(
    bounds: Rect, title: string, subtitle: string, color: string, metrics: PhoneMetrics,
  ): void;
  drawNotificationChip(
    rect: Rect, label: string, accent: string, fontPx: number,
  ): void;
  staggerAlpha(index: number, delayMs: number, durationMs: number): number;
  drawCircularPortrait(path: string, x: number, y: number, size: number): void;
  drawNotificationPageNavButton(
    rect: Rect, label: string, desktop: boolean,
    style: { fill: string; stroke: string; radius?: number }, textColor: string,
  ): void;
  rafsbyPortrait: HTMLImageElement | null;
}

export function drawPhoneFriendsScreen(
  deps: PhoneFriendsDeps,
  ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
  scrX: number, scrY: number, scrW: number, scrH: number,
  contentX: number, contentW: number, contentRight: number,
  metrics: PhoneMetrics,
): void {
  const desktop = metrics.desktop;

  // ── Friend Detail View ──
  if (model.phoneFriendId != null) {
    const friend = model.friendsList.find(f => f.odId === model.phoneFriendId);
    if (friend) {
      deps.drawPhoneFriendDetail(ctx, model, uiRects, scrX, scrY, scrW, scrH, contentX, contentW, contentRight, metrics, friend);
      return;
    }
  }

  // ── Inline Add Friend Mode ──
  if (model.phoneAddFriendMode) {
    deps.drawPhoneAddFriendInline(ctx, model, uiRects, scrX, scrY, scrW, scrH, contentX, contentW, contentRight, metrics);
    return;
  }

  // ── Refer a Friend Page ──
  if (model.phoneReferMode) {
    deps.drawPhoneReferFriend(ctx, model, uiRects, scrX, scrY, scrW, scrH, contentX, contentW, contentRight, metrics);
    return;
  }

  let curY = scrY + metrics.topPad;
  ctx.fillStyle = "#64d8ff";
  ctx.font = `bold ${metrics.titleFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("FRIENDS", scrX + scrW / 2, curY);
  curY += metrics.titleGap;

  const btnW = desktop ? 46 : 40;
  const btnH = metrics.inlineButtonH;

  // ── Pending Requests ──
  const pending = model.pendingRequests;
  if (pending.length > 0) {
    ctx.fillStyle = "#ff9966";
    ctx.font = `bold ${metrics.sectionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(`REQUESTS (${pending.length})`, contentX, curY);
    curY += desktop ? 12 : 11;

    for (let i = 0; i < Math.min(pending.length, 3); i++) {
      const req = pending[i];
      const rowH = metrics.rowH;
      if (curY + rowH > scrY + scrH - 30) break;

      if (desktop) {
        deps.drawNotificationPanel(
          { x: contentX, y: curY, w: contentW, h: rowH },
          { fill: "rgba(16,29,49,0.95)", stroke: "rgba(255,153,102,0.3)", accent: "#ff9966", radius: metrics.cardRadius }
        );
      } else {
        ctx.fillStyle = "#162030";
        ctx.fillRect(contentX, curY, contentW, rowH);
        ctx.fillStyle = "#ff9966";
        ctx.fillRect(contentX, curY, 3, rowH);
      }

      // Name
      ctx.fillStyle = "#c8d8f0";
      ctx.font = `bold ${metrics.labelFontPx}px ${GAME_FONT}`;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(req.username.toUpperCase().slice(0, desktop ? 12 : 10), contentX + 10, curY + rowH / 2 - (desktop ? 3 : 0));
      if (desktop) {
        ctx.fillStyle = "#7fa0c2";
        ctx.font = `${metrics.helperFontPx}px ${GAME_FONT}`;
        ctx.fillText(formatDerivedAccountLevel(req.level, "LVL "), contentX + 10, curY + rowH / 2 + 7);
      }

      // Accept button
      const inlineY = curY + (rowH - btnH) / 2;
      const accRect: Rect = { x: contentX + contentW - btnW * 2 - 4, y: inlineY, w: btnW, h: btnH };
      (uiRects as Record<string, Rect>)[`friendAccept${i}`] = accRect;
      if (desktop) {
        deps.drawNotificationPanel(accRect, { fill: "#0a2a10", stroke: "#4dff88", radius: 4 });
      } else {
        ctx.fillStyle = "#0a2a10";
        ctx.fillRect(accRect.x, accRect.y, accRect.w, accRect.h);
        ctx.strokeStyle = "#4dff88";
        ctx.lineWidth = 1;
        ctx.strokeRect(accRect.x, accRect.y, accRect.w, accRect.h);
      }
      ctx.fillStyle = "#4dff88";
      ctx.font = `bold ${metrics.helperFontPx}px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("ACCEPT", accRect.x + accRect.w / 2, accRect.y + accRect.h / 2);

      // Decline button
      const decRect: Rect = { x: contentX + contentW - btnW, y: inlineY, w: btnW, h: btnH };
      (uiRects as Record<string, Rect>)[`friendDecline${i}`] = decRect;
      if (desktop) {
        deps.drawNotificationPanel(decRect, { fill: "#2a1010", stroke: "#ff6644", radius: 4 });
      } else {
        ctx.fillStyle = "#2a1010";
        ctx.fillRect(decRect.x, decRect.y, decRect.w, decRect.h);
        ctx.strokeStyle = "#ff6644";
        ctx.strokeRect(decRect.x, decRect.y, decRect.w, decRect.h);
      }
      ctx.fillStyle = "#ff6644";
      ctx.fillText("DECLINE", decRect.x + decRect.w / 2, decRect.y + decRect.h / 2);

      curY += rowH + metrics.rowGap;
    }
    curY += metrics.sectionGap;
  }

  // ── Friends List ──
  const friends = model.friendsList;
  if (friends.length === 0 && pending.length === 0) {
    deps.drawNotificationEmptyState(
      { x: contentX, y: curY + 8, w: contentW, h: scrY + scrH - curY - 34 },
      "NO FRIENDS YET",
      desktop ? "USE + ADD FRIEND TO START" : "ADD A FRIEND TO BEGIN",
      "#64d8ff",
      metrics
    );
    return;
  }

  if (friends.length > 0) {
    // Divider
    ctx.fillStyle = "#2a3a55";
    for (let dx = contentX; dx < contentRight; dx += 4) {
      ctx.fillRect(dx, curY, 2, 1);
    }
    curY += metrics.sectionGap;
  }

  const portraitSize = desktop ? 30 : 28;
  const rowH = metrics.messageCardH;
  for (let i = 0; i < friends.length; i++) {
    const f = friends[i];
    if (curY + rowH > scrY + scrH - 26) break;

    const alpha = deps.staggerAlpha(i, 40, 180);
    if (alpha <= 0) { curY += rowH + metrics.rowGap; continue; }
    if (alpha < 1) { ctx.save(); ctx.globalAlpha = alpha; }

    // Row bg
    if (desktop) {
      deps.drawNotificationPanel(
        { x: contentX, y: curY, w: contentW, h: rowH },
        {
          fill: i % 2 === 0 ? "rgba(22,32,48,0.98)" : "rgba(26,36,56,0.98)",
          stroke: f.online ? "rgba(77,255,136,0.28)" : "rgba(92,140,212,0.18)",
          accent: f.online ? "#4dff88" : "#3a4a6a",
          radius: metrics.cardRadius
        }
      );
    } else {
      ctx.fillStyle = i % 2 === 0 ? "#162030" : "#1a2438";
      ctx.fillRect(contentX, curY, contentW, rowH);
      // Green accent bar for online friends (like inbox accent bars)
      ctx.fillStyle = f.online ? "#4dff88" : "#3a4a6a";
      ctx.fillRect(contentX, curY, 3, rowH);
    }

    // Beast portrait (circular crop like top bar profile)
    const pX = contentX + (desktop ? 6 : 8);
    const pY = curY + (rowH - portraitSize) / 2;
    if ((f as { isNpc?: boolean }).isNpc && deps.rafsbyPortrait) {
      // Draw Rafsby portrait flipped in circular crop
      ctx.save();
      ctx.beginPath();
      ctx.arc(pX + portraitSize / 2, pY + portraitSize / 2, portraitSize / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.translate(pX + portraitSize, pY);
      ctx.scale(-1, 1);
      ctx.drawImage(deps.rafsbyPortrait, 0, 0, portraitSize, portraitSize);
      ctx.restore();
    } else {
      const imgPath = resolveCharacterImagePath(f.equippedSkin);
      deps.drawCircularPortrait(imgPath, pX, pY, portraitSize);
    }

    // Online indicator (overlaid on portrait corner)
    ctx.fillStyle = f.online ? "#4dff88" : "#3a4a6a";
    ctx.beginPath();
    ctx.arc(pX + portraitSize - 2, pY + portraitSize - 2, desktop ? 3 : 2.5, 0, Math.PI * 2);
    ctx.fill();

    // Name + level/guide badge
    const textX = pX + portraitSize + 10;
    ctx.fillStyle = f.online ? "#c8d8f0" : "#6a7a90";
    ctx.font = `bold ${metrics.labelFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(f.username.toUpperCase().slice(0, desktop ? 12 : 10), textX, curY + rowH / 2 - (desktop ? 6 : 5));
    ctx.fillStyle = "#5a7a9a";
    ctx.font = `${metrics.metaFontPx}px ${GAME_FONT}`;
    ctx.fillText(
      (f as { isNpc?: boolean }).isNpc ? "ONLINE" : formatDerivedAccountLevel(f.level, "LVL "),
      textX, curY + rowH / 2 + (desktop ? 8 : 7),
    );

    // Chevron (tap to open detail)
    ctx.fillStyle = "#5a7a9a";
    ctx.font = `bold ${desktop ? 10 : 8}px ${GAME_FONT}`;
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.fillText("\u203A", contentRight - 4, curY + rowH / 2);

    // Register tappable row rect
    (uiRects as Record<string, Rect>)[`friendRow${i}`] = { x: contentX, y: curY, w: contentW, h: rowH };

    if (alpha < 1) ctx.restore();
    curY += rowH + metrics.rowGap;
  }

  // Bottom buttons — pinned
  const addBtnH = metrics.ctaH;
  const btnGap = 4;
  const halfW = Math.floor((contentW - btnGap) / 2);
  const bottomY = scrY + scrH - addBtnH - (model.friendsTotalCount > 5 ? 24 : 6);

  // ADD FRIEND button (left)
  const addRect: Rect = { x: contentX, y: bottomY, w: halfW, h: addBtnH };
  uiRects.openAddFriend = addRect;
  ctx.fillStyle = "#0a2a10";
  ctx.fillRect(addRect.x, addRect.y, addRect.w, addRect.h);
  ctx.strokeStyle = "#4dff88";
  ctx.lineWidth = 1;
  ctx.strokeRect(addRect.x, addRect.y, addRect.w, addRect.h);
  ctx.fillStyle = "#4dff88";
  ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("+ ADD", addRect.x + addRect.w / 2, addRect.y + addRect.h / 2);

  // REFER A FRIEND button (right)
  const referRect: Rect = { x: contentX + halfW + btnGap, y: bottomY, w: halfW, h: addBtnH };
  uiRects.phoneReferFriend = referRect;
  ctx.fillStyle = "#1a1a30";
  ctx.fillRect(referRect.x, referRect.y, referRect.w, referRect.h);
  ctx.strokeStyle = "#a78bfa";
  ctx.lineWidth = 1;
  ctx.strokeRect(referRect.x, referRect.y, referRect.w, referRect.h);
  ctx.fillStyle = "#a78bfa";
  ctx.fillText("REFER", referRect.x + referRect.w / 2, referRect.y + referRect.h / 2);

  // Pagination
  if (model.friendsTotalCount > 5) {
    const navY = scrY + scrH - (desktop ? 20 : 18);
    const totalPages = Math.ceil(model.friendsTotalCount / 5);
    const page = model.friendsPage;

    ctx.fillStyle = "#5a7a9a";
    ctx.font = `${metrics.helperFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`${page + 1}/${totalPages}`, scrX + scrW / 2, navY + 8);

    if (page > 0) {
      const prevRect: Rect = { x: contentX, y: navY, w: desktop ? 34 : 30, h: metrics.navButtonH };
      uiRects.friendsPagePrev = prevRect;
      deps.drawNotificationPageNavButton(
        prevRect,
        "\u25C4",
        desktop,
        { fill: "#1a2a40", stroke: "#64d8ff", radius: 4 },
        "#64d8ff"
      );
    }
    if (page < totalPages - 1) {
      const nextRect: Rect = {
        x: contentX + contentW - (desktop ? 34 : 30),
        y: navY,
        w: desktop ? 34 : 30,
        h: metrics.navButtonH
      };
      uiRects.friendsPageNext = nextRect;
      deps.drawNotificationPageNavButton(
        nextRect,
        "\u25BA",
        desktop,
        { fill: "#1a2a40", stroke: "#64d8ff", radius: 4 },
        "#64d8ff"
      );
    }
  }
}
